import React, { useEffect, useRef } from 'react';
import { X, MessageCircle, Calendar, BookOpen, Package, Users, Settings, MapPin, Image } from 'lucide-react';

export type PageId = 'chats' | 'slots' | 'bookings' | 'inventory' | 'assignment' | 'admin-work' | 'destinations' | 'image-upload';

export const menuItems: { id: PageId; label: string; icon: React.ReactNode }[] = [
  { id: 'chats', label: 'Chats', icon: <MessageCircle className="h-5 w-5" /> },
  { id: 'slots', label: 'Trip Slots', icon: <Calendar className="h-5 w-5" /> },
  { id: 'bookings', label: 'Bookings', icon: <BookOpen className="h-5 w-5" /> },
  { id: 'inventory', label: 'Inventory', icon: <Package className="h-5 w-5" /> },
  { id: 'assignment', label: 'Assignment', icon: <Users className="h-5 w-5" /> },
  { id: 'admin-work', label: 'Admin Work', icon: <Settings className="h-5 w-5" /> },
  { id: 'destinations', label: 'Destinations', icon: <MapPin className="h-5 w-5" /> },
  { id: 'image-upload', label: 'Image Upload', icon: <Image className="h-5 w-5" /> },
];

interface NavMenuProps {
  isOpen: boolean;
  onClose: () => void;
  currentPage: PageId;
  onPageChange: (page: PageId) => void;
  mobileOnly?: boolean;
}

export const NavMenu: React.FC<NavMenuProps> = ({
  isOpen,
  onClose,
  currentPage,
  onPageChange,
  mobileOnly = false,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const responsiveClass = mobileOnly ? 'lg:hidden' : '';

  const handlePageChange = (page: PageId) => {
    onPageChange(page);
    onClose();
  };

  // Focus first menu item on open
  useEffect(() => {
    if (isOpen) {
      const firstItem = menuRef.current?.querySelector('[role="menuitem"]') as HTMLElement;
      firstItem?.focus();
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const items = menuRef.current?.querySelectorAll('[role="menuitem"]');
      if (!items) return;
      const itemsArray = Array.from(items) as HTMLElement[];
      const currentIndex = itemsArray.indexOf(document.activeElement as HTMLElement);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          itemsArray[(currentIndex + 1) % itemsArray.length]?.focus();
          break;
        case 'ArrowUp':
          e.preventDefault();
          itemsArray[currentIndex <= 0 ? itemsArray.length - 1 : currentIndex - 1]?.focus();
          break;
        case 'Home':
          e.preventDefault();
          itemsArray[0]?.focus();
          break;
        case 'End':
          e.preventDefault();
          itemsArray[itemsArray.length - 1]?.focus();
          break;
        case 'Escape':
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-40 animate-fade-in ${responsiveClass}`}
        onClick={onClose}
      />

      {/* Menu */}
      <div
        ref={menuRef}
        className={`fixed top-0 right-0 h-full w-80 bg-white dark:bg-gray-800 shadow-xl z-50 animate-slide-in-right ${responsiveClass}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Navigation</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Menu Items */}
        <div className="p-4">
          <nav className="space-y-2" role="menu">
            {menuItems.map((item) => (
              <button
                key={item.id}
                role="menuitem"
                tabIndex={-1}
                onClick={() => handlePageChange(item.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  currentPage === item.id
                    ? 'bg-primary-50 text-primary-700 border border-primary-200'
                    : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                <div className={`${
                  currentPage === item.id ? 'text-primary-600' : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {item.icon}
                </div>
                <span className="font-medium">{item.label}</span>
                {currentPage === item.id && (
                  <div className="ml-auto w-2 h-2 bg-primary-600 rounded-full" />
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>
    </>
  );
};
